import { Component, EventEmitter, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Sidebar } from 'primeng/primeng';
import { SelectItem } from 'primeng/components/common/selectitem';

import { TagService } from '../../../../services/tag.service';
import { StepComponent } from '../../../step.component';
import { StrtPriority } from '../../../../../shared/models/plan/StrtPriority';
import { PriorityService } from '../../../../services/priority.service';
import { BehaviorSubject } from 'rxjs/index';
import { StrtObjective } from '../../../../../shared/models/plan/StrtObjective';


@Component({
  selector: 'app-objective',
  templateUrl: './objective.component.html',
  styleUrls: ['./objective.component.scss']
})
export class ObjectiveComponent implements OnInit, OnChanges {
  readonly MAX_TEXT_LENGTH = 150;

  @Output() create = new EventEmitter<StrtObjective>();
  @Output() createError = new EventEmitter<Error>();
  @Output() edit = new EventEmitter<StrtObjective>();
  @Output() editError = new EventEmitter<Error>();
  @Output() cancel = new EventEmitter<void>();

  private _objectiveForm: FormGroup;
  private _isActive: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isActive$ = this._isActive.asObservable();
  private isEdit = false;
  private pristineObjective: StrtObjective;

  prioritySelectItems: SelectItem[];
  tagSelectItems: SelectItem[] = [{label: 'Select a Tag', value: undefined}];

  // return a list of priorities that are legal for the user to select
  private static toPrioritySelectItems(priorities: StrtPriority[]): SelectItem[] {
    return [{label: 'Select a Strategic Theme', value: undefined}, ...PriorityService.getPrioritySelectItems(priorities)];
  }

  constructor(private formBuilder: FormBuilder, private tagService: TagService) { }

  ngOnInit() {
    this.createForm();
    this.tagService.getSQFTags().subscribe(it => {
      this.tagSelectItems.push(...this.tagService.getTagSelectItems(it));
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    StepComponent.logNgOnChanges(changes);
  }

  editObjective(objective: StrtObjective, priorities: StrtPriority[]) {
    this.prioritySelectItems = ObjectiveComponent.toPrioritySelectItems(priorities);
    this._isActive.next(true);
    this.isEdit = true;
    this.pristineObjective = objective;
    this.objectiveForm.patchValue({
      priority: this.pristineObjective.priorityId,
      text: this.pristineObjective.text,
      tag: this.pristineObjective.tagId
    });
  }

  createObjective(priorities: StrtPriority[]) {
    this.prioritySelectItems = ObjectiveComponent.toPrioritySelectItems(priorities);
    this._isActive.next(true);
    this.isEdit = false;
    this.pristineObjective = undefined;
  }

  get objectiveForm() {
    return this._objectiveForm;
  }

  get priority() {
    return this.objectiveForm.get('priority');
  }

  get text() {
    return this.objectiveForm.get('text');
  }

  get tag() {
    return this.objectiveForm.get('tag');
  }

  get headerContent() {
    return this.isEdit ? 'Edit Objective' : 'Add Objective';
  }

  get isSubmitDisabled() {
    return !(this.objectiveForm.status === 'VALID' &&
        !this.objectiveForm.pristine &&
        this.objectiveForm.dirty);
  }

  get textRemainingCharacters() {
    return this.MAX_TEXT_LENGTH - this.text.value.length;
  }

  onShowSidebar(sidebar: Sidebar) {
    sidebar.maskClickListener();
  }

  onHideSidebar() {
    this.objectiveForm.reset({priority: '', text: '', tag: undefined});
    this.isEdit = false;
    this.pristineObjective = undefined;
  }

  onSubmit() {
    const objective = this.formToObjective();
    if (this.isEdit) {
      this.edit.emit(objective);
    } else {
      this.create.emit(objective);
    }
    this._isActive.next(false);
  }

  onCancel() {
    this.cancel.emit();
    this._isActive.next(false);
  }

  private createForm() {
    this._objectiveForm = this.formBuilder.group({
      priority: ['', Validators.required],
      text: ['', [Validators.maxLength(this.MAX_TEXT_LENGTH), Validators.required]],
      tag: ['', [Validators.required]]
    });
  }

  private formToObjective(): StrtObjective {
    const strategyId = this.pristineObjective ? this.pristineObjective.strategyId : undefined;
    return {
      strategyId,
      priorityId: this.priority.value,
      objectiveId: this.pristineObjective ? this.pristineObjective.objectiveId : undefined,
      text: this.text.value,
      tagId: this.tag.value
    };

  }


}
